// src/features/affiliate/format.ts
// Pure helpers shared across the affiliate feature — no React imports.

import type { Entity } from '@core/types/entity';
import type { Money } from '@core/types/affiliate';

export interface ScopeIds {
  platformId: string;
  productId: string;
  tenantId: string;
}

/** Build a fully-populated Entity reference from an id and the current scope. */
export function makeEntity(id: string, typeName: string, scope: ScopeIds): Entity {
  return {
    type_id: '',
    type_name: typeName,
    id,
    platform_id: scope.platformId,
    tenant_id: scope.tenantId,
    product_id: scope.productId,
  };
}

export function errMessage(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

/** Strip an ENUM_PREFIX_ off a proto enum value and lowercase it for i18n keys. */
export function enumKey(value: string | undefined, prefix: string): string {
  if (!value) return 'unspecified';
  return value.startsWith(prefix) ? value.slice(prefix.length).toLowerCase() : value.toLowerCase();
}

/** Money is stored as an integer scaled by decimal_places (minor units). */
export function formatMoney(money: Money | undefined, locale = 'en'): string {
  if (!money) return '—';
  const amount = (money.amount ?? 0) / Math.pow(10, money.decimal_places ?? 2);
  const code = currencyCode(money.currency);
  try {
    if (code) {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(amount);
    }
  } catch {
    // Unknown/unsupported ISO code — fall through to plain number formatting.
  }
  return amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Map the CURRENCY_XXX enum to its ISO 4217 code, or undefined if unspecified/unmapped. */
export function currencyCode(c?: string): string | undefined {
  if (!c || c === 'CURRENCY_UNSPECIFIED') return undefined;
  const code = c.replace('CURRENCY_', '');
  return code || undefined;
}

export function fmtDate(value: string | undefined, locale = 'en'): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return value;
  }
}

export function fmtDateTime(value: string | undefined, locale = 'en'): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(locale, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return value;
  }
}

/** Days between now and an ISO timestamp — negative if in the past. */
export function daysUntil(value: string | undefined): number | null {
  if (!value) return null;
  const target = new Date(value).getTime();
  if (Number.isNaN(target)) return null;
  return Math.ceil((target - Date.now()) / (24 * 60 * 60 * 1000));
}

// ---------------------------------------------------------------------------
// "My affiliate profile" pointer
// ---------------------------------------------------------------------------
// AffiliateService has no "get my affiliate" query — only GetAffiliate(id),
// gated by the owner/viewer relation OpenFGA grants at CreateAffiliate time.
// We remember the id locally once the partner has applied so the dashboard
// can find their own record again without an admin-only list call.

function storageKey(profileId: string): string {
  return `affiliate_id:${profileId}`;
}

export function getStoredAffiliateId(profileId: string | null | undefined): string | null {
  if (!profileId || typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(storageKey(profileId));
  } catch {
    return null;
  }
}

export function setStoredAffiliateId(profileId: string | null | undefined, affiliateId: string): void {
  if (!profileId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(profileId), affiliateId);
  } catch {
    // Storage unavailable — the dashboard will just ask again next visit.
  }
}

export function clearStoredAffiliateId(profileId: string | null | undefined): void {
  if (!profileId || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(storageKey(profileId));
  } catch {
    // Ignore.
  }
}

// ---------------------------------------------------------------------------
// Pagination + filter params
// ---------------------------------------------------------------------------
// The generated List* endpoints type their params as PaginatedRequest, but
// the wire format (see ApiClient.buildUrl) flattens any plain object one
// level deep, so extra filter keys travel fine as long as we name them after
// the REST query_mapping key (not the proto field name). This mirrors the
// cast pattern already used by the calendar feature.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function listParams(pagination: { cursor?: string; limit?: number }, filters: Record<string, any> = {}): any {
  return {
    pagination: { cursor: pagination.cursor ?? '', limit: pagination.limit ?? 50, direction: 'forward' },
    ...filters,
  };
}
